// Tab "Shotlist": shots per scene, plus de brug naar Cadre (storyboard) heen en terug.
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputBase, MenuItem, Select, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import { bevestig } from '../../../utils/bevestig';
import { meldFout } from '../../../utils/apiFout';
import { api, cadreImport, downloadBlob, exportJson, nieuwShot, wijzigShot, LIJN, SHOT_TYPES, STIL } from '../breakdownApi';
import { Kaart, Kop, Leeg, Primair, Rij, Secundair } from '../onderdelen';

function Omschrijving({ shot, onBewaar }) {
  const [tekst, setTekst] = useState(shot.description);
  useEffect(() => { setTekst(shot.description); }, [shot.description]);
  return (
    <InputBase fullWidth multiline value={tekst} onChange={(e) => setTekst(e.target.value)}
      onBlur={() => { if (tekst.trim() && tekst !== shot.description) onBewaar(tekst); else setTekst(shot.description); }}
      inputProps={{ 'aria-label': 'Omschrijving shot' }} sx={{ fontSize: 13.5, px: 0.75, borderRadius: 1, '&:hover': { bgcolor: '#f3f4f6' } }} />
  );
}

export default function ShotlistTab({ productie, scenes, shots, setShots, herlaad }) {
  const [nieuw, setNieuw] = useState({ scene_number: '', shot_type: 'Wide / Totaal', description: '' });
  const bestandRef = useRef(null);
  const vervang = (s) => setShots((lijst) => lijst.map((x) => (x.id === s.id ? s : x)));

  async function voegToe() {
    if (!nieuw.description.trim()) return;
    try {
      const s = await nieuwShot(productie.id, { ...nieuw, scene_number: nieuw.scene_number === '' ? null : nieuw.scene_number });
      setShots([...shots, s]);
      setNieuw((n) => ({ ...n, description: '' }));
    } catch (e) { meldFout(e, 'Shot toevoegen'); }
  }

  async function bewaar(s, body) {
    try { vervang(await wijzigShot(s.id, body)); } catch (e) { meldFout(e, 'Shot opslaan'); }
  }

  async function verwijder(s) {
    const ok = await bevestig({ titel: 'Shot verwijderen?', tekst: `"${s.description}" wordt verwijderd.`, knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      await api(`/shots/${s.id}`, { method: 'DELETE', wat: 'Shot verwijderen' });
      setShots((lijst) => lijst.filter((x) => x.id !== s.id));
    } catch (e) { meldFout(e, 'Shot verwijderen'); }
  }

  async function naarCadre() {
    try {
      const data = await exportJson(productie.id, 'cadre');
      downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), `Cadre - ${productie.name}.json`);
    } catch (e) { meldFout(e, 'Cadre-export'); }
  }

  async function uitCadre(e) {
    const bestand = e.target.files?.[0];
    e.target.value = '';
    if (!bestand) return;
    const ok = await bevestig({ titel: 'Importeren uit Cadre?', tekst: `Scenes, shots en cast uit "${bestand.name}" worden toegevoegd. Wat al bestaat wordt overgeslagen.`, knop: 'Importeren' });
    if (!ok) return;
    try {
      const r = await cadreImport(productie.id, bestand);
      toast.success(`Geïmporteerd: ${r.scenes_added} scenes, ${r.shots_added} shots, ${r.cast_added} cast`);
      herlaad();
    } catch (err) { meldFout(err, 'Cadre-import'); }
  }

  const groepen = {};
  [...shots].forEach((s) => { const k = s.scene_number ?? '—'; (groepen[k] = groepen[k] || []).push(s); });
  const sleutels = Object.keys(groepen).sort((a, b) => (a === '—') - (b === '—') || Number(a) - Number(b));
  const sceneNaam = (n) => scenes.find((s) => String(s.number) === String(n))?.heading;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', border: `1px solid ${LIJN}`, borderRadius: 2, px: 1.5, py: 1, mb: 2.5 }}>
        <Typography sx={{ flex: 1, fontSize: 13, color: STIL }}><b>Cadre</b> — scenes, shots en cast uitwisselen met het storyboard.</Typography>
        <Secundair onClick={naarCadre}>Naar Cadre</Secundair>
        <Secundair onClick={() => bestandRef.current?.click()}>Uit Cadre…</Secundair>
        <input ref={bestandRef} type="file" accept="application/json,.json" hidden onChange={uitCadre} />
      </Box>

      {shots.length === 0 && <Leeg>Nog geen shots.</Leeg>}
      {sleutels.map((k) => (
        <Box key={k} sx={{ mb: 2 }}>
          <Kop>{k === '—' ? 'Zonder scene' : `Scene ${k}${sceneNaam(k) ? ` — ${sceneNaam(k)}` : ''}`}</Kop>
          <Kaart>
            {groepen[k].map((s) => (
              <Rij key={s.id}>
                <Select size="small" variant="standard" disableUnderline value={s.shot_type} onChange={(e) => bewaar(s, { shot_type: e.target.value })}
                  sx={{ width: 150, fontSize: 12.5, fontWeight: 600, color: STIL }} inputProps={{ 'aria-label': 'Soort shot' }}>
                  {SHOT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
                <Omschrijving shot={s} onBewaar={(d) => bewaar(s, { description: d })} />
                <IconButton size="small" aria-label="Shot verwijderen" onClick={() => verwijder(s)}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Rij>
            ))}
          </Kaart>
        </Box>
      ))}

      <Kaart sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, flexWrap: 'wrap' }}>
        <Select size="small" value={nieuw.scene_number} displayEmpty onChange={(e) => setNieuw((n) => ({ ...n, scene_number: e.target.value }))}
          sx={{ minWidth: 120, fontSize: 13 }} inputProps={{ 'aria-label': 'Scene' }}>
          <MenuItem value="">Geen scene</MenuItem>
          {[...scenes].sort((a, b) => a.number - b.number).map((s) => <MenuItem key={s.id} value={s.number}>Scene {s.number}</MenuItem>)}
        </Select>
        <Select size="small" value={nieuw.shot_type} onChange={(e) => setNieuw((n) => ({ ...n, shot_type: e.target.value }))}
          sx={{ minWidth: 150, fontSize: 13 }} inputProps={{ 'aria-label': 'Soort shot' }}>
          {SHOT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </Select>
        <InputBase sx={{ flex: 1, minWidth: 180, fontSize: 13.5 }} placeholder="Omschrijving van het shot…" value={nieuw.description}
          onChange={(e) => setNieuw((n) => ({ ...n, description: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }}
          inputProps={{ 'aria-label': 'Omschrijving nieuw shot' }} />
        <Primair onClick={voegToe} disabled={!nieuw.description.trim()}>Toevoegen</Primair>
      </Kaart>
    </Box>
  );
}
